import { type ReactNode, useRef } from 'react'
import { ContentLabel } from '@/components/ContentLabel'
import styles from './FirmDashboardOverviewSidebar.module.css'
import { Icon, type IconName } from '@/components/Icon'
import {
  Section,
  SectionContent,
  SectionHeader,
  useSection,
} from '@/components/Section'
import { ScrollView } from '@/components/ScrollView'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { Link } from '@/components/Link'
import { formatMoney } from '@/utils/money-util'
import cx from 'classnames'
import {
  Dropdown,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { Tooltip } from '@/components/Tooltip'
import { dateFormatters } from '@/utils/date-util'

export interface FirmDashboardOverviewSidebarProps {
  children: ReactNode
}

export function FirmDashboardOverviewSidebar({
  children,
}: FirmDashboardOverviewSidebarProps) {
  return (
    <>
      <ContentLabel>Overview</ContentLabel>

      <div className={styles.overviewContainer}>{children}</div>
    </>
  )
}

interface OverviewAmountItemProps {
  amount: number
  icon: IconName
  title: ReactNode
}
function OverviewContractItem({
  amount,
  icon,
  title,
}: OverviewAmountItemProps) {
  return (
    <div className={styles.contractItem}>
      <Icon name={icon} className={styles.contractIcon} />
      <h2 className={styles.contractTitle}>{formatMoney(amount)}</h2>
      <p className={styles.contractType}>{title}</p>
    </div>
  )
}

interface ContractUsageItemProps {
  contractUsage: number
  submittedJobsTotalPrice: number
  closedJobsTotalPrice: number
  setupFees: number
  vmFees: number
  vmFeesQuantity: number
  vmFeesAmountPerUnit: number
}

function ContractUsageItem({
  contractUsage,
  submittedJobsTotalPrice,
  closedJobsTotalPrice,
  setupFees,
  vmFees,
  vmFeesQuantity,
  vmFeesAmountPerUnit,
}: ContractUsageItemProps) {
  return (
    <div className={styles.contractItem}>
      <Icon name={'analytics'} className={styles.contractIcon} />
      <h2 className={styles.contractTitle}>{formatMoney(contractUsage)}</h2>
      <div className={styles.feesDropdown}>
        <p className={styles.contractType}>Contract usage</p>
        <Dropdown
          position="bottom"
          button={
            <IconButton
              name="dropdown"
              label="Show contract usage breakdown"
              size={16}
            />
          }
        >
          <div className={styles.contractBreakdown}>
            <div className={styles.contractBreakdownItem}>
              <DropdownItemTitle>Submitted jobs</DropdownItemTitle>
              <DropdownItemDescription>
                {formatMoney(submittedJobsTotalPrice)}
              </DropdownItemDescription>
            </div>
            <div className={styles.contractBreakdownItem}>
              <DropdownItemTitle>Closed jobs</DropdownItemTitle>
              <DropdownItemDescription>
                {formatMoney(closedJobsTotalPrice)}
              </DropdownItemDescription>
            </div>
            {setupFees > 0 && (
              <div className={styles.contractBreakdownItem}>
                <DropdownItemTitle>Setup fee</DropdownItemTitle>
                <DropdownItemDescription>
                  {formatMoney(setupFees)}
                </DropdownItemDescription>
              </div>
            )}
            {vmFees > 0 && (
              <div className={styles.vmFeeBreakdown}>
                <div>
                  <DropdownItemTitle>Virtual machines</DropdownItemTitle>
                  <DropdownItemDescription>
                    {formatMoney(vmFees)}
                  </DropdownItemDescription>
                </div>
                <VmFeesDropdown
                  quantity={vmFeesQuantity}
                  amountPerUnit={vmFeesAmountPerUnit}
                />
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  )
}

interface VmFeesDropdownProps {
  quantity: number
  amountPerUnit: number
}

function VmFeesDropdown({ quantity, amountPerUnit }: VmFeesDropdownProps) {
  return (
    <Dropdown
      position="right-start"
      button={
        <IconButton
          name="arrow"
          label="Show VM fees breakdown"
          size={24}
          color="var(--brand-primary)"
        />
      }
    >
      <div className={styles.contractBreakdown}>
        <div className={styles.contractBreakdownItem}>
          <DropdownItemTitle>Quantity</DropdownItemTitle>
          <DropdownItemDescription>{quantity}</DropdownItemDescription>
        </div>
        <div className={styles.contractBreakdownItem}>
          <DropdownItemTitle>Individual price</DropdownItemTitle>
          <DropdownItemDescription>
            {formatMoney(amountPerUnit)}
          </DropdownItemDescription>
        </div>
      </div>
    </Dropdown>
  )
}

interface OverviewContractProps {
  children: ReactNode
}

function OverviewContract({ children }: OverviewContractProps) {
  return <div className={styles.contract}>{children}</div>
}

interface ContractDateProps {
  date: Date
  title: string
  align?: 'right' | 'left'
}

function ContractDate({ date, title, align }: ContractDateProps) {
  return (
    <div className={cx(styles.contractDateContainer, align && styles[align])}>
      <div className={styles.contractDate}>
        {dateFormatters.dateLong.format(date)}
      </div>
      <div className={styles.contractDateTitle}>{title}</div>
    </div>
  )
}

interface ContractDateRangeProps {
  dateStart: Date | null
  dateEnd: Date | null
}

function ContractDateRange({ dateEnd, dateStart }: ContractDateRangeProps) {
  if (!dateEnd || !dateStart) {
    return <></>
  }

  return (
    <div>
      <div className={styles.contractSeparator} />
      <div className={styles.contractDateRange}>
        <ContractDate date={dateStart} title="Contract start date" />
        <Icon name="back" rotate={180} color="var(--grey500)" size={24} />
        <ContractDate date={dateEnd} title="Contract end date" align="right" />
      </div>
    </div>
  )
}

export interface FirmDashboardOverviewSidebarHeaderProps {
  greeting: string
  submittedJobsTotalPrice: number
  closedJobsTotalPrice: number
  canViewContractAmount: boolean
  contractUsage: number
  contractTotal: number
  setupFees: number
  vmFees: number
  vmFeesQuantity: number
  vmFeesAmountPerUnit: number
  isContractConfigEnabled: boolean
  contractStartDate: Date | null
  contractEndDate: Date | null
}

export function FirmDashboardOverviewSidebarHeader({
  greeting,
  submittedJobsTotalPrice,
  closedJobsTotalPrice,
  canViewContractAmount,
  contractUsage,
  contractTotal,
  setupFees,
  vmFees,
  vmFeesQuantity,
  vmFeesAmountPerUnit,
  isContractConfigEnabled,
  contractStartDate,
  contractEndDate,
}: FirmDashboardOverviewSidebarHeaderProps) {
  return (
    <div className={styles.header}>
      <h1 className={styles.title}>{greeting}</h1>
      <p className={styles.description}>
        Here is an overview of your contract, along with pending items that need
        your attention.
      </p>
      <OverviewContract>
        <OverviewContractItem
          amount={submittedJobsTotalPrice}
          icon="jobs"
          title={
            <div className={styles.submittedJobsTooltip}>
              <span>Submitted jobs</span>{' '}
              <Tooltip content="Jobs In Progress" placement="top">
                <Icon name="info" size={20} color="var(--brand-primary)" />
              </Tooltip>
            </div>
          }
        />
        <OverviewContractItem
          amount={closedJobsTotalPrice}
          icon="payout"
          title="Completed Jobs"
        />
        {canViewContractAmount && (
          <>
            <ContractUsageItem
              contractUsage={contractUsage}
              submittedJobsTotalPrice={submittedJobsTotalPrice}
              closedJobsTotalPrice={closedJobsTotalPrice}
              setupFees={setupFees}
              vmFees={vmFees}
              vmFeesQuantity={vmFeesQuantity}
              vmFeesAmountPerUnit={vmFeesAmountPerUnit}
            />
            <OverviewContractItem
              amount={contractTotal}
              icon="documents"
              title="Contract total"
            />
          </>
        )}
      </OverviewContract>

      {isContractConfigEnabled && (
        <ContractDateRange
          dateStart={contractStartDate}
          dateEnd={contractEndDate}
        />
      )}
    </div>
  )
}

export interface FirmDashboardOverviewSidebarJobListProps {
  openItemsJobsCount: number
  draftReviewJobsCount: number
  openItemsJobs: ReactNode
  draftInReviewJobs: ReactNode
  scrollLoader: ReactNode
}

export function FirmDashboardOverviewSidebarJobList({
  openItemsJobsCount,
  draftReviewJobsCount,
  openItemsJobs,
  draftInReviewJobs,
  scrollLoader,
}: FirmDashboardOverviewSidebarJobListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const openItemsSection = useSection(false)
  const draftReviewItemsSection = useSection(false)

  return (
    <div className={styles.body}>
      <ScrollView ref={scrollRef}>
        <Section {...openItemsSection}>
          <SectionHeader>
            Open items
            <span className={styles.jobCount}>
              &nbsp;({openItemsJobsCount})
            </span>
          </SectionHeader>
          <SectionContent>{openItemsJobs}</SectionContent>
        </Section>

        <Section {...draftReviewItemsSection}>
          <SectionHeader>
            Draft review
            <span className={styles.jobCount}>
              &nbsp;({draftReviewJobsCount})
            </span>
          </SectionHeader>

          <SectionContent>{draftInReviewJobs}</SectionContent>
        </Section>

        {scrollLoader}
      </ScrollView>
    </div>
  )
}

export interface FirmDashboardOverviewSidebarJobProps {
  to: string
  displayName: string
  lastUpdate: string
  avatarSubjects: AvatarSubject[]
}

export function FirmDashboardOverviewSidebarJob({
  to,
  displayName,
  lastUpdate,
  avatarSubjects,
}: FirmDashboardOverviewSidebarJobProps) {
  return (
    <Link className={styles.item} to={to}>
      <div className={styles.avatarContainer}>
        <Avatar size={32} subject={avatarSubjects} />
      </div>

      <div className={styles.primaryInfo}>
        <span className={styles.primaryText}>{displayName}</span>
        <span className={styles.secondaryInfo}>{lastUpdate}</span>
      </div>
    </Link>
  )
}
